import React, { useState, useEffect, useMemo, useRef } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { toast } from 'react-toastify'
import { useFormik } from 'formik'
import { Modal, ModalActions } from '../../../components/Modal'
import { handleError } from '../../../errors/handleError'
import { Input } from '../../../components/Inputs/Input'
import { InboxType } from '../../../../server/models/Inbox'
import { useInboxesQuery } from '../../../requests/inboxes'
import { createInboxMember } from '../../../requests/inboxMembers'
import { OneInboxToJoin } from './OneInboxToJoin'
import { CreatedInboxSuccessModal } from '../CreatedInboxSuccessModal'
import { useMyInboxes } from '../../../contexts/MyInboxesContext'
import { InboxMemberType } from '../../../../server/models/InboxMember'
import { Skeleton } from '../../../components/Skeleton'
import { createSearchTerm } from '../../../requests/analytics'
import { useAuth } from '../../../contexts/useAuthUser'
import { createPaymentSession, useEarlyAccessQuery } from '../../../requests/billing'
import './styles.css'

type Props = {
  onInboxesJoined: (inboxesJoined: InboxMemberType[]) => void
}

export const JoinInboxButton: React.FC<Props> = ({
  onInboxesJoined
}) => {
  const { inboxMemberships } = useMyInboxes()
  const { authUser, subscription } = useAuth()
  const location = useLocation()
  const { data: earlyAccessData } = useEarlyAccessQuery(authUser?._id || '', {
    enabled: !!authUser?._id
  })
  const isEarlyAccess = earlyAccessData?.earlyAccess ?? false

  const [formModalShowing, setFormModalShowing] = useState(false)
  const [joinedInboxesModalShowing, setJoinedInboxesModalShowing] = useState(false)
  const [subscribeModalShowing, setSubscribeModalShowing] = useState(false)
  const [createdInbox, setCreatedInbox] = useState<InboxType>()
  const [paymentSessionData, setPaymentSessionData] = useState<{
    clientSecret: string,
    publishableKey: string,
    pricingTableId: string
  } | null>(null)
  const [loadingPaymentSession, setLoadingPaymentSession] = useState(false)

  const { data: inboxes = [], isLoading: loadingInboxes } = useInboxesQuery()

  const formik = useFormik({
    initialValues: {
      search: '',
      selectedInboxIds: []
    },
    onSubmit: async (values, formik) => {
      if (values.selectedInboxIds.length === 0) {
        return toast.error('Please select at least one folder to join.')
      }

      // If user has an active subscription, allow unlimited folders
      if (!subscription) {
        // Check if user is in free trial
        const isInFreeTrial = authUser?.freeTrialEndsAt && new Date(authUser.freeTrialEndsAt) > new Date()
        
        // Check if attempting to join more than 1 inbox
        const totalInboxesAfterJoin = inboxMemberships.length + values.selectedInboxIds.length
        const wouldExceedLimit = totalInboxesAfterJoin > 1

        // If not in free trial and would exceed limit, show subscribe modal
        if (!isInFreeTrial && wouldExceedLimit) {
          setFormModalShowing(false)
          setSubscribeModalShowing(true)
          return
        }
      }

      try {
        const res = await Promise.all(
          values.selectedInboxIds.map(inboxId => createInboxMember(inboxId))
        )

        toast.success(`🎉 Successfully joined ${values.selectedInboxIds.length} folder${ values.selectedInboxIds.length === 1 ? '' : 's' }.`)

        onInboxesJoined(
          res.map(r => r.data)
        )

        setJoinedInboxesModalShowing(true)
        setFormModalShowing(false)
        formik.resetForm()
      } catch (err) {
        handleError(err, 'Something went wrong joining these folders. Please try again later.')
      }
    }
  })

  const showFormModal = () => {
    setFormModalShowing(true)
  }

  const closeModal = () => {
    if (formik.isSubmitting) {
      return
    }

    formik.resetForm()
    setFormModalShowing(false)
    setJoinedInboxesModalShowing(false)
    setSubscribeModalShowing(false)
    setCreatedInbox(undefined)
    setPaymentSessionData(null)
  }

  // Load payment session when subscribe modal opens
  useEffect(() => {
    if (subscribeModalShowing && !paymentSessionData && !loadingPaymentSession && formik.values.selectedInboxIds.length > 0) {
      setLoadingPaymentSession(true)
      createPaymentSession(formik.values.selectedInboxIds)
        .then(res => {
          setPaymentSessionData({
            clientSecret: res.data.clientSecret,
            publishableKey: res.data.publishableKey,
            pricingTableId: res.data.pricingTableId
          })
        })
        .catch(err => {
          handleError(err, 'Failed to load subscription options. Please try again later.')
          setSubscribeModalShowing(false)
        })
        .finally(() => {
          setLoadingPaymentSession(false)
        })
    }
  }, [subscribeModalShowing, paymentSessionData, loadingPaymentSession, formik.values.selectedInboxIds])

  useEffect(() => {
    if (location.search.includes('isNewUser')) {
      showFormModal()
    }
  }, [location.search])

  const toggleInboxSelected = (inbox: InboxType) => {
    if (formik.values.selectedInboxIds.includes(inbox._id)) {
      formik.setFieldValue('selectedInboxIds', formik.values.selectedInboxIds.filter(id => id !== inbox._id))
    } else {
      formik.setFieldValue('selectedInboxIds', [...formik.values.selectedInboxIds, inbox._id])
    }
  }

  const inboxesToShow = useMemo(() => {
    const inboxIAmMemberOf = inboxes.filter((inbox) => {
      return !inboxMemberships.some(membership => 
        membership.inbox?._id === inbox._id
      )
    })

    if (formik.values.search === '') {
      return inboxIAmMemberOf
    }
    return inboxIAmMemberOf.filter((inbox) => {
      return inbox.name.toLowerCase().includes(formik.values.search.toLowerCase())
    })
  }, [inboxes, inboxMemberships, formik.values.search])

  // Track search terms with no results (debounced)
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null)
  const lastTrackedSearchRef = useRef<string>('')

  useEffect(() => {
    const searchTerm = formik.values.search.trim()
    const hasNoResults = !loadingInboxes && searchTerm.length >= 3 && inboxesToShow.length === 0
    const isNewSearch = searchTerm !== lastTrackedSearchRef.current

    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }

    // Only track if: search >= 3 chars, no results, and it's a new search term
    if (hasNoResults && isNewSearch) {
      debounceTimerRef.current = setTimeout(async () => {
        try {
          await createSearchTerm(searchTerm)
          lastTrackedSearchRef.current = searchTerm
        } catch (err) {
          // Silently fail - don't show error to user for analytics
          console.error('Failed to track search term:', err)
        }
      }, 1000) // 1 second debounce
    }

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
    }
  }, [formik.values.search, inboxesToShow.length, loadingInboxes])

  return (
    <>
      <Modal isOpen={formModalShowing} onRequestClose={closeModal}>
        <form onSubmit={formik.handleSubmit}>
          <h2>Choose Folders to Join</h2>
          <p>
              What type of content are you interested in? Search for topics and join folders to get started.
          </p>
          <Input
            name="search"
            type="search"
            formik={formik}
            placeholder="Search a topic..."
            disabled={formik.isSubmitting}
            label=""
          />
          <div className="join-inboxes-container">
            {loadingInboxes && (
              <div className="loading-inboxes-message">
                <p>Loading folders...</p>
                <Skeleton width={200} />
                <Skeleton width={200} />
                <Skeleton width={200} />
              </div>
            )}
            {!loadingInboxes && formik.values.search && inboxesToShow.length === 0 && (
              <p className="no-inboxes-found-message">😔 No folders found matching "{formik.values.search}"</p>
            )}
            {!loadingInboxes && inboxesToShow.map((inbox) => (
              <OneInboxToJoin
                key={inbox._id}
                inbox={inbox}
                selected={formik.values.selectedInboxIds.includes(inbox._id)}
                onClick={toggleInboxSelected}
              />
            ))}
          </div>
          <ModalActions
            onCancel={closeModal}
            loading={formik.isSubmitting}
            submitText={formik.values.selectedInboxIds.length > 1 ? 'Join Folders' : 'Join Folder'}
            submit
          />
        </form>
      </Modal>

      <CreatedInboxSuccessModal
        isOpen={!!createdInbox}
        onRequestClose={closeModal}
        createdInbox={createdInbox}
        loading={formik.isSubmitting}
      />

      <Modal isOpen={joinedInboxesModalShowing} onRequestClose={closeModal}>
        <h2>🚀 You're ready to go</h2>
        <h4>Check out the <Link to="/app/content">Content</Link> page for daily new content!</h4>
        <ModalActions
          onSubmit={closeModal}
          loading={formik.isSubmitting}
          submitText="Got it"
        />
      </Modal>

      <Modal isOpen={subscribeModalShowing} onRequestClose={closeModal}>
        <h2>Upgrade to Join More Folders</h2>
        {isEarlyAccess ? (
          <p>
            Thank you for being part of early access! You can keep your existing folders, but to join more you must upgrade to join more folders.
          </p>
        ) : (
          <p>
            Free users can only join one folder. Upgrade to join unlimited folders and access all features.
          </p>
        )}
        {loadingPaymentSession && (
          <div>
            <p>Loading subscription options...</p>
            <Skeleton width={400} />
          </div>
        )}
        {!loadingPaymentSession && paymentSessionData && (
          <stripe-pricing-table
            pricing-table-id={paymentSessionData.pricingTableId}
            publishable-key={paymentSessionData.publishableKey}
            customer-session-client-secret={paymentSessionData.clientSecret}
          />
        )}
        <ModalActions
          onCancel={closeModal}
        />
      </Modal>

      <button className="primary plausible-event-name=ClickJoinInbox" onClick={showFormModal}>
          Join a Folder...
      </button>
    </>
  )
}

